import React, { useContext, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { AppContext } from "../../context/AppContextProvider";
import useAxiosInstance from "../../lib/useAxiosInstance";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";

/* ─── helpers ────────────────────────────────────────────────────── */
const nc = (x) => {
  if (x === null || x === undefined || String(x).trim() === "" || x === "null") return "—";
  return String(x).trim();
};
const blank = (x) =>
  !x || String(x).trim() === "" || x === "null" || x === null || x === undefined;

const PARTY_COLORS = {
  भाजपा: "#FF6B1A", BJP: "#FF6B1A",
  सपा: "#FCD34D", SP: "#FCD34D",
  बसपा: "#A78BFA", BSP: "#A78BFA",
  कांग्रेस: "#34D399", INC: "#34D399",
  AIMIM: "#60A5FA",
};
const partyColor = (p = "") => {
  for (const [k, v] of Object.entries(PARTY_COLORS)) {
    if (String(p).toLowerCase().includes(k.toLowerCase())) return v;
  }
  return "#94A3B8";
};

function Badge({ label, color }) {
  if (blank(label)) return null;
  const c = color || partyColor(label);
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, letterSpacing: "0.02em",
      background: c + "22", border: `1px solid ${c}55`, color: c,
    }}>{label}</span>
  );
}

function SecHead({ icon, title, sub, accent, isDark }) {
  const ac = accent || "#FF6B1A";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        background: ac + "22", border: `1px solid ${ac}44`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#F0F6FF" : "#001D30", lineHeight: 1.2 }}>{title}</div>
        {sub && <div style={{ fontSize: 13, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${ac}44, transparent)`, marginLeft: 8 }} />
    </div>
  );
}

function StatCard({ label, value, accent, isDark }) {
  const ac = accent || "#FF6B1A";
  const bg = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.04)";
  const border = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,72,119,0.12)";
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 16,
      padding: "18px 20px", flex: 1, minWidth: 110,
      borderTop: `3px solid ${ac}`,
    }}>
      <div style={{ fontSize: 26, fontWeight: 900, color: isDark ? "#F0F6FF" : "#001D30", lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</div>
    </div>
  );
}

function Meter({ label, value, level, isDark }) {
  const color = level === "H" ? "#34D399" : level === "M" ? "#FBBF24" : "#F87171";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3, color: isDark ? "rgba(200,218,255,0.7)" : "#6B7280" }}>
        <span>{label}</span><span style={{ fontWeight: 700, color }}>{value}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ width: `${value}%`, height: "100%", background: color, borderRadius: 4 }} />
      </div>
    </div>
  );
}

function VoteSharePie({ shares, isDark }) {
  const data = (shares || []).filter((s) => !blank(s.party) && !blank(s.pct));
  if (!data.length) return null;
  const textColor = isDark ? "#CBD5E1" : "#374151";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <div style={{ width: 160, height: 160, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" outerRadius={70} paddingAngle={3} dataKey="pct" nameKey="party" strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={partyColor(d.party)} />)}
            </Pie>
            <Tooltip contentStyle={{ background: isDark ? "#0D1730" : "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13 }} itemStyle={{ color: textColor }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.map((d) => (
          <div key={d.party} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: partyColor(d.party) }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: partyColor(d.party) }}>{d.party}</span>
            <span style={{ fontSize: 13, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280" }}>{d.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Assembly report view (matches the "whole assembly" JSON schema) ──── */
function AssemblyPlaybookView({ data, isDark }) {
  const d = data || {};
  const meta = d.meta || {};
  const exec = d.executive_snapshot || {};
  const landscape = d.political_landscape || {};
  const caste = d.caste_dynamics || {};
  const candidates = d.candidate_matrix || [];
  const mla = d.mla_scorecard || {};
  const issues = d.key_issues || {};
  const sentiment = d.voter_sentiment_map || [];
  const insights = d.strategic_insights || [];
  const strategy = d.actionable_strategy || {};
  const winPath = d.win_path_2027 || {};

  const P = isDark ? "#F0F6FF" : "#001D30";
  const S = isDark ? "rgba(200,218,255,0.78)" : "#374151";
  const M = isDark ? "rgba(150,175,220,0.55)" : "#6B7280";
  const B = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,72,119,0.1)";
  const card = { background: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${B}`, borderRadius: 20, padding: "24px 28px", marginBottom: 20 };

  const currentMla = exec.current_mla_2022 || {};

  return (
    <div style={{ paddingBottom: 60 }}>
      {/* ── HERO ── */}
      <div style={{ background: isDark ? "linear-gradient(135deg,rgba(0,40,80,0.96),rgba(0,18,42,0.99))" : "linear-gradient(135deg,#EFF6FF,#F0FDF4)", border: `1px solid ${B}`, borderRadius: 24, padding: "32px 32px 28px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#FF6B1A,#FF3F88,#8B5CF6)" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--saffron)", marginBottom: 8 }}>
            ELECTION MODE — {nc(meta.cycle)}
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, color: P, margin: "0 0 6px", lineHeight: 1.05, letterSpacing: "-0.03em" }}>{nc(meta.constituency)}</h1>
          <div style={{ fontSize: 14, color: S, marginBottom: 16 }}>{nc(meta.district)} · {nc(meta.lok_sabha_seat)} · {nc(meta.state)}</div>

          {!blank(meta.cover_insight) && (
            <div style={{ background: "linear-gradient(135deg,rgba(255,107,26,0.12),rgba(255,63,136,0.06))", border: "1px solid rgba(255,107,26,0.25)", borderRadius: 14, padding: "14px 18px", fontSize: 14, color: isDark ? "rgba(255,215,185,0.92)" : "#92400e", lineHeight: 1.7, marginBottom: 18 }}>
              🔍 {meta.cover_insight}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {!blank(exec.seat_classification?.value) && (
              <StatCard label={exec.seat_classification.tag || "Seat"} value={exec.seat_classification.value} accent="#FF6B1A" isDark={isDark} />
            )}
            {!blank(exec.total_electors_2024) && <StatCard label="Total Electors" value={exec.total_electors_2024} accent="#3B82F6" isDark={isDark} />}
            {!blank(exec.turnout_2022_pct) && <StatCard label="2022 Turnout" value={`${exec.turnout_2022_pct}%`} accent="#34D399" isDark={isDark} />}
            {!blank(currentMla.margin_votes) && <StatCard label="2022 Margin" value={currentMla.margin_votes} accent="#F87171" isDark={isDark} />}
          </div>
        </div>
      </div>

      {/* ── EXECUTIVE SNAPSHOT ── */}
      {(currentMla.name || exec.key_swing_factors?.length) && (
        <div style={card}>
          <SecHead icon="📌" title="Executive Snapshot" sub="Current seat status & swing factors" isDark={isDark} accent="#3B82F6" />
          {!blank(currentMla.name) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${B}` }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: partyColor(currentMla.party) + "33", border: `2px solid ${partyColor(currentMla.party)}55`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: partyColor(currentMla.party) }}>{currentMla.name[0]}</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: P }}>{currentMla.name} <Badge label={currentMla.party} /></div>
                <div style={{ fontSize: 13, color: M }}>Sitting MLA (2022) · Margin {currentMla.margin_votes} votes ({currentMla.margin_pct}%)</div>
              </div>
            </div>
          )}
          {!blank(exec.primary_contest) && <div style={{ fontSize: 14, color: S, marginBottom: 10 }}><strong style={{ color: M }}>Primary Contest: </strong>{exec.primary_contest}</div>}
          {exec.spoilers?.length > 0 && (
            <div style={{ marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: M, fontWeight: 700 }}>Spoilers:</span>
              {exec.spoilers.map((s, i) => <Badge key={i} label={s} />)}
            </div>
          )}
          {exec.key_swing_factors?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {exec.key_swing_factors.map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 14, color: S, lineHeight: 1.7 }}>
                  <span style={{ color: "var(--saffron)", fontWeight: 800, flexShrink: 0 }}>{i + 1}.</span>{f}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── POLITICAL LANDSCAPE ── */}
      {(landscape.parties?.length > 0 || landscape.vote_share_2022?.length > 0) && (
        <div style={card}>
          <SecHead icon="🗺️" title="Political Landscape" sub="Party roles, vote blocks & power centers" isDark={isDark} accent="#6366F1" />
          {landscape.vote_share_2022?.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>2022 Vote Share</div>
              <VoteSharePie shares={landscape.vote_share_2022} isDark={isDark} />
            </div>
          )}
          {landscape.parties?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12, marginBottom: 20 }}>
              {landscape.parties.map((p, i) => {
                const pc = partyColor(p.party);
                return (
                  <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px", borderLeft: `3px solid ${pc}` }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: P, marginBottom: 6 }}>{p.party}</div>
                    {!blank(p.role) && <div style={{ fontSize: 13, color: S, marginBottom: 6 }}>{p.role}</div>}
                    {!blank(p.note) && <div style={{ fontSize: 12, color: M, lineHeight: 1.6 }}>{p.note}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {landscape.vote_blocks?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Vote Blocks</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12, marginBottom: 20 }}>
                {landscape.vote_blocks.map((v, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: P }}>{v.block} <span style={{ fontWeight: 600, color: M, fontSize: 12 }}>({v.size})</span></div>
                    <div style={{ marginTop: 6 }}><Badge label={v.alignment} color="#60A5FA" /></div>
                    {!blank(v.note) && <div style={{ fontSize: 12, color: S, marginTop: 8, lineHeight: 1.6 }}>{v.note}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
          {landscape.power_centers?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Power Centers</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12 }}>
                {landscape.power_centers.map((pcItem, i) => (
                  <div key={i} style={{ background: isDark ? "rgba(167,139,250,0.06)" : "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 14, padding: "14px 16px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: P }}>{pcItem.name}</div>
                    {!blank(pcItem.role) && <div style={{ fontSize: 12, color: M, marginTop: 4 }}>{pcItem.role}</div>}
                    {!blank(pcItem.note) && <div style={{ fontSize: 12, color: S, marginTop: 6, lineHeight: 1.6 }}>{pcItem.note}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CASTE DYNAMICS ── */}
      {(caste.composition?.length > 0 || caste.alignment_detail?.length > 0) && (
        <div style={card}>
          <SecHead icon="👥" title="Caste Dynamics" sub="Community composition & alignment" isDark={isDark} accent="#8B5CF6" />
          {caste.composition?.length > 0 && (
            <div style={{ overflowX: "auto", marginBottom: 20 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>{["Community", "Approx Voters", "Alignment"].map((h) => <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", textAlign: "left", borderBottom: `2px solid ${B}` }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {caste.composition.map((r, i) => (
                    <tr key={i} style={{ borderBottom: i < caste.composition.length - 1 ? `1px solid ${B}` : "none" }}>
                      <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 15, color: P }}>{nc(r.community)}</td>
                      <td style={{ padding: "12px 12px", fontSize: 14, color: S }}>{nc(r.approx_voters)}</td>
                      <td style={{ padding: "12px 12px" }}><Badge label={r.alignment} color="#60A5FA" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {caste.alignment_detail?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
              {caste.alignment_detail.map((r, i) => (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: P, marginBottom: 6 }}>{r.community}</div>
                  {!blank(r.risk) && <div style={{ fontSize: 12, color: "#F87171", marginBottom: 6 }}>⚠ {r.risk}</div>}
                  {!blank(r.note) && <div style={{ fontSize: 13, color: S, lineHeight: 1.65 }}>{r.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CANDIDATE MATRIX ── */}
      {candidates.length > 0 && (
        <div style={card}>
          <SecHead icon="🏛️" title="Candidate Matrix" sub="Contender strengths, weaknesses & win probability" isDark={isDark} accent="#F59E0B" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
            {candidates.map((c, i) => {
              const pc = partyColor(c.party);
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 16, padding: "18px 20px", borderLeft: `3px solid ${pc}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: pc + "33", border: `2px solid ${pc}55`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: pc, flexShrink: 0 }}>{c.initials || c.name?.[0]}</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: P }}>{c.name}</div>
                      <Badge label={c.party} />
                    </div>
                    {!blank(c.win_probability_pct) && (
                      <div style={{ marginLeft: "auto", textAlign: "right" }}>
                        <div style={{ fontSize: 20, fontWeight: 900, color: "#34D399" }}>{c.win_probability_pct}%</div>
                        <div style={{ fontSize: 10, color: M }}>{c.win_probability_label || "win prob."}</div>
                      </div>
                    )}
                  </div>
                  {!blank(c.role) && <div style={{ fontSize: 13, color: S, marginBottom: 10 }}>{c.role}</div>}
                  {c.meters && (
                    <div style={{ marginBottom: 10 }}>
                      {Object.entries(c.meters).map(([k, v]) => (
                        <Meter key={k} label={k.replace(/_/g, " ")} value={v} level={c.meter_levels?.[k]} isDark={isDark} />
                      ))}
                    </div>
                  )}
                  {c.strengths?.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#34D399", marginBottom: 4 }}>STRENGTHS</div>
                      {c.strengths.map((s, si) => <div key={si} style={{ fontSize: 12, color: S, lineHeight: 1.6 }}>• {s}</div>)}
                    </div>
                  )}
                  {c.weaknesses?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#F87171", marginBottom: 4 }}>WEAKNESSES</div>
                      {c.weaknesses.map((s, si) => <div key={si} style={{ fontSize: 12, color: S, lineHeight: 1.6 }}>• {s}</div>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MLA SCORECARD ── */}
      {mla.mla && (
        <div style={card}>
          <SecHead icon="📈" title="MLA Scorecard" sub={`${mla.mla} (${mla.party}) — ${mla.tenure}`} isDark={isDark} accent="#34D399" />
          {!blank(mla.overall_score) && (
            <div style={{ marginBottom: 20 }}>
              <StatCard label="Overall Score" value={mla.overall_score} accent="#34D399" isDark={isDark} />
            </div>
          )}
          {mla.scores?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12, marginBottom: 20 }}>
              {mla.scores.map((s, i) => (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "12px 16px" }}>
                  <div style={{ fontSize: 13, color: S, marginBottom: 6 }}>{s.category}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: P }}>{s.score}/5</div>
                </div>
              ))}
            </div>
          )}
          {mla.field_intelligence_notes?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {mla.field_intelligence_notes.map((n, i) => (
                <div key={i} style={{ display: "flex", gap: 10, background: n.flag === "positive" ? "rgba(52,211,153,0.06)" : "rgba(248,113,113,0.05)", border: `1px solid ${n.flag === "positive" ? "rgba(52,211,153,0.2)" : "rgba(248,113,113,0.15)"}`, borderRadius: 12, padding: "12px 14px" }}>
                  <span>{n.flag === "positive" ? "✅" : "⚠️"}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: P, marginBottom: 4 }}>{n.observation}</div>
                    {!blank(n.implication) && <div style={{ fontSize: 12, color: M, lineHeight: 1.6 }}>{n.implication}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── KEY ISSUES ── */}
      {Object.keys(issues).length > 0 && (
        <div style={card}>
          <SecHead icon="⚠️" title="Key Issues" sub="Civic, economic, governance & political sentiment" isDark={isDark} accent="#F87171" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16 }}>
            {Object.entries(issues).map(([cat, list]) => (
              <div key={cat}>
                <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{cat.replace(/_/g, " ")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(list || []).map((it, i) => (
                    <div key={i} style={{ background: isDark ? "rgba(248,113,113,0.05)" : "rgba(248,113,113,0.04)", border: "1px solid rgba(248,113,113,0.15)", borderRadius: 12, padding: "10px 12px" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: P }}>{it.issue}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        {it.impact && <Badge label={it.impact} color={it.impact === "HIGH" ? "#F87171" : "#FBBF24"} />}
                        {it.trigger && <Badge label={it.trigger} color="#94A3B8" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── VOTER SENTIMENT MAP ── */}
      {sentiment.length > 0 && (
        <div style={card}>
          <SecHead icon="🧭" title="Voter Sentiment Map" sub="Segment-wise sentiment breakdown" isDark={isDark} accent="#06B6D4" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 14 }}>
            {sentiment.map((s, i) => (
              <div key={i} style={{ background: isDark ? "rgba(6,182,212,0.06)" : "rgba(6,182,212,0.05)", border: "1px solid rgba(6,182,212,0.2)", borderRadius: 16, padding: "16px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: P }}>{s.segment}</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#06B6D4" }}>{s.pct}%</div>
                </div>
                {!blank(s.insight) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7, marginBottom: 8 }}>{s.insight}</div>}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(s.tags || []).map((t, ti) => <Badge key={ti} label={t} color="#60A5FA" />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── STRATEGIC INSIGHTS ── */}
      {insights.length > 0 && (
        <div style={card}>
          <SecHead icon="💡" title="Strategic Insights" isDark={isDark} accent="#A78BFA" />
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {insights.map((ins, i) => (
              <div key={i} style={{ display: "flex", gap: 14, paddingBottom: 14, borderBottom: i < insights.length - 1 ? `1px solid ${B}` : "none" }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: "linear-gradient(135deg,#8B5CF6,#EC4899)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13 }}>{ins.num}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: P, marginBottom: 4 }}>{ins.insight}</div>
                  {!blank(ins.detail) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7 }}>{ins.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ACTIONABLE STRATEGY ── */}
      {Object.keys(strategy).length > 0 && (
        <div style={card}>
          <SecHead icon="🎯" title="Actionable Strategy" isDark={isDark} accent="#6366F1" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 16 }}>
            {Object.entries(strategy).map(([cat, list]) => (
              <div key={cat}>
                <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{cat.replace(/_/g, " ")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(list || []).map((it, i) => (
                    <div key={i} style={{ fontSize: 13, color: S, lineHeight: 1.65 }}>• {it}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WIN PATH ── */}
      {winPath.steps?.length > 0 && (
        <div style={{ background: isDark ? "rgba(22,34,80,0.7)" : "rgba(220,228,255,0.6)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 20, padding: "28px 32px", position: "relative", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#6366F1,#8B5CF6,#FF6B1A)" }} />
          <SecHead icon="🏆" title="Win Path 2027" isDark={isDark} accent="#6366F1" />
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            {winPath.steps.map((step) => (
              <div key={step.step} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0, background: step.critical ? "linear-gradient(135deg,#F87171,#EF4444)" : "linear-gradient(135deg,#34D399,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 13 }}>{step.step}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: P }}>{step.title} {step.critical && <Badge label="Critical" color="#F87171" />}</div>
                  <div style={{ fontSize: 13, color: S, lineHeight: 1.7 }}>{step.detail}</div>
                </div>
              </div>
            ))}
          </div>
          {winPath.outcome && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <StatCard label="Outcome" value={winPath.outcome.title} accent="#34D399" isDark={isDark} />
              <StatCard label="Target Margin" value={winPath.outcome.target_margin_votes} accent="#6366F1" isDark={isDark} />
              <StatCard label="2022 Margin" value={winPath.outcome.margin_2022_votes} accent="#F87171" isDark={isDark} />
            </div>
          )}
          {!blank(winPath.win_formula) && (
            <div style={{ fontSize: 14, color: isDark ? "rgba(205,218,255,0.9)" : "#2A3F72", lineHeight: 1.9, fontStyle: "italic" }}>{winPath.win_formula}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main modal ─────────────────────────────────────────────────── */
export default function ElectionModePlaybookModal({ open, onClose, electionModeType, selectedConstituency, selectedBlock }) {
  const { themes } = useContext(AppContext);
  const isDark = themes === "darkMode";
  const axiosInstance = useAxiosInstance();

  const [electionMode, setElectionMode] = useState({ parliamentaryReports: [], assemblyPlaybooks: [], blockPlaybooks: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true); setError(null);
        const res = await axiosInstance.get("api/playbook/my-playbook");
        setElectionMode(res.data?.data?.electionMode || { parliamentaryReports: [], assemblyPlaybooks: [], blockPlaybooks: [] });
      } catch (e) {
        setError(e?.response?.data?.message || "No playbook found for your account.");
      } finally { setLoading(false); }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const matchByConstituency = (list, name) =>
    (list || []).find((p) => p.constituency && name && p.constituency.trim().toLowerCase() === name.trim().toLowerCase());
  const matchByBlock = (list, name) =>
    (list || []).find((p) => p.block && name && p.block.trim().toLowerCase() === name.trim().toLowerCase());

  // Determine which playbook to show based on election mode + current selection.
  const activePlaybook = useMemo(() => {
    if (electionModeType === "state") {
      // Select any constituency -> show its assembly playbook.
      if (selectedConstituency) {
        return matchByConstituency(electionMode.assemblyPlaybooks, selectedConstituency) || null;
      }
      return null;
    }
    if (electionModeType === "assembly") {
      // If a block is selected and a block playbook exists for it, prefer that.
      if (selectedBlock) {
        const blockMatch = matchByBlock(electionMode.blockPlaybooks, selectedBlock);
        if (blockMatch) return blockMatch;
      }
      // Otherwise show the client's own whole-assembly playbook (top of page).
      return electionMode.assemblyPlaybooks?.[0] || null;
    }
    if (electionModeType === "parliamentary") {
      if (selectedConstituency) {
        const acMatch = matchByConstituency(electionMode.assemblyPlaybooks, selectedConstituency);
        if (acMatch) return acMatch;
      }
      return electionMode.parliamentaryReports?.[0] || null;
    }
    return null;
  }, [electionMode, electionModeType, selectedConstituency, selectedBlock]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const bg = isDark ? "#060D1A" : "#F0F6FC";
  const topbarBg = isDark ? "#142240" : "rgba(240,246,252,0.95)";
  const borderCol = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,72,119,0.12)";
  const textPrimary = isDark ? "#F0F6FF" : "#001D30";
  const textMuted = isDark ? "rgba(180,205,240,0.7)" : "#6B7280";
  const textSecondary = isDark ? "rgba(220,235,255,0.88)" : "#374151";

  const title = activePlaybook?.data?.meta?.constituency || activePlaybook?.name || "Election Mode Playbook";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: isDark ? "#060D1A" : "rgba(225,238,250,0.97)", display: "flex", flexDirection: "column" }}>
      <div style={{ height: 60, flexShrink: 0, display: "flex", alignItems: "center", paddingLeft: 20, paddingRight: 20, gap: 14, background: topbarBg, borderBottom: `1px solid ${borderCol}` }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, letterSpacing: "-0.01em" }}>{title}</div>
          <div style={{ fontSize: 11, color: textMuted, letterSpacing: "0.04em" }}>Election Mode Playbook · {electionModeType}</div>
        </div>
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,72,119,0.08)", border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,72,119,0.15)"}`, cursor: "pointer", color: textPrimary }}>
          <X size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: bg, position: "relative" }}>
        {isDark && (
          <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: `radial-gradient(ellipse 65% 50% at 20% 15%,rgba(99,102,241,0.09) 0%,transparent 60%),radial-gradient(ellipse 50% 40% at 80% 80%,rgba(255,107,26,0.07) 0%,transparent 55%),linear-gradient(160deg,#07101E 0%,#0C1A2E 55%,#07101E 100%)` }} />
        )}
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1400, margin: "0 auto", padding: "24px 32px" }}>
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${isDark ? "rgba(255,107,26,0.15)" : "rgba(0,72,119,0.15)"}`, borderTopColor: "var(--saffron)", animation: "spin 0.8s linear infinite" }} />
              <span style={{ color: textMuted, fontSize: 15 }}>Loading playbook…</span>
            </div>
          )}

          {!loading && error && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 18, textAlign: "center" }}>
              <div style={{ fontSize: 48 }}>📋</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>No Playbook Found</div>
              <div style={{ fontSize: 15, color: textSecondary, maxWidth: 360, lineHeight: 1.85 }}>{error}</div>
            </div>
          )}

          {!loading && !error && activePlaybook && (
            <AssemblyPlaybookView data={activePlaybook.data} isDark={isDark} />
          )}

          {!loading && !error && !activePlaybook && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 18, textAlign: "center" }}>
              <div style={{ fontSize: 48 }}>📋</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>
                {electionModeType === "state" ? "Select a Constituency" : "No Playbook Available"}
              </div>
              <div style={{ fontSize: 15, color: textSecondary, maxWidth: 400, lineHeight: 1.85 }}>
                {electionModeType === "state"
                  ? "Choose a constituency from the top filter bar to view its playbook."
                  : "No playbook has been uploaded for this scope yet. Please contact Admin."}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
